/**
 * Centralized color system for Beyond Code
 * Maps to CSS variables defined in Global.css
 */

// Primary Blue Colors
export const PRIMARY_COLORS = {
    PRIMARY_950: 'var(--color-primary-950)',
    PRIMARY_900: 'var(--color-primary-900)',
    PRIMARY_800: 'var(--color-primary-800)',
    PRIMARY: 'var(--color-primary)',
    PRIMARY_700: 'var(--color-primary-700)',
    PRIMARY_600: 'var(--color-primary-600)',
    PRIMARY_500: 'var(--color-primary-500)',
    PRIMARY_400: 'var(--color-primary-400)',
    PRIMARY_300: 'var(--color-primary-300)',
    PRIMARY_200: 'var(--color-primary-200)',
    PRIMARY_100: 'var(--color-primary-100)',
    PRIMARY_50: 'var(--color-primary-50)',
} as const;

// Accent Orange Colors
export const ACCENT_COLORS = {
    ACCENT_950: 'var(--color-accent-950)',
    ACCENT_900: 'var(--color-accent-900)',
    ACCENT: 'var(--color-accent)',
    ACCENT_800: 'var(--color-accent-800)',
    ACCENT_700: 'var(--color-accent-700)',
    ACCENT_600: 'var(--color-accent-600)',
    ACCENT_500: 'var(--color-accent-500)',
    ACCENT_400: 'var(--color-accent-400)',
    ACCENT_300: 'var(--color-accent-300)',
    ACCENT_200: 'var(--color-accent-200)',
    ACCENT_100: 'var(--color-accent-100)',
    ACCENT_50: 'var(--color-accent-50)',
} as const;

// Grayscale / Neutral Colors
export const GRAY_COLORS = {
    GRAY_950: 'var(--color-gray-950)',
    GRAY_900: 'var(--color-gray-900)',
    GRAY_800: 'var(--color-gray-800)',
    GRAY_700: 'var(--color-gray-700)',
    GRAY_600: 'var(--color-gray-600)',
    GRAY_500: 'var(--color-gray-500)',
    GRAY_400: 'var(--color-gray-400)',
    GRAY_300: 'var(--color-gray-300)',
    GRAY_200: 'var(--color-gray-200)',
    GRAY_100: 'var(--color-gray-100)',
    GRAY_50: 'var(--color-gray-50)',
} as const;

// Text Colors
export const TEXT_COLORS = {
    WHITE: 'var(--white-text)',
} as const;

// Layout Values
export const LAYOUT_VALUES = {
    BUTTON_RADIUS: 'var(--button-radius)',
    ONBOARDING_BUTTON_RADIUS: 'var(--onboarding-button-radius)',
    BUTTON_GLOW: 'var(--button-glow)',
} as const;

export const BACKGROUND_COLORS = {
    ONBOARDING_BACKGROUND: 'var(--onboarding-background-color)'
}

// Combined colors object for easy access
export const COLORS = {
    ...PRIMARY_COLORS,
    ...ACCENT_COLORS,
    ...GRAY_COLORS,
    ...TEXT_COLORS,
    // Semantic aliases
    PRIMARY_DEFAULT: PRIMARY_COLORS.PRIMARY,
    ACCENT_DEFAULT: ACCENT_COLORS.ACCENT,
    BLACK: GRAY_COLORS.GRAY_950,
    WHITE: TEXT_COLORS.WHITE,
    // Common use cases
    TEXT_PRIMARY: GRAY_COLORS.GRAY_900,
    TEXT_SECONDARY: GRAY_COLORS.GRAY_700,
    TEXT_DISABLED: GRAY_COLORS.GRAY_500,
    BACKGROUND_PRIMARY: GRAY_COLORS.GRAY_50,
    BACKGROUND_SECONDARY: GRAY_COLORS.GRAY_100,
    BORDER_DEFAULT: GRAY_COLORS.GRAY_300,
    BORDER_LIGHT: GRAY_COLORS.GRAY_200,
    ONBOARDING_BACKGROUND: BACKGROUND_COLORS.ONBOARDING_BACKGROUND
} as const;

// Type for all color keys
export type ColorKey = keyof typeof COLORS;
export type PrimaryColorKey = keyof typeof PRIMARY_COLORS;
export type AccentColorKey = keyof typeof ACCENT_COLORS;
export type GrayColorKey = keyof typeof GRAY_COLORS;

/**
 * Utility to apply opacity to a CSS color variable
 * @param color - The color variable (e.g., COLORS.PRIMARY)
 * @param opacity - Opacity value between 0 and 1
 * @returns RGB color string with opacity
 */
export const withOpacity = (color: string, opacity: number): string => {
    // This would need to be implemented with actual RGB values
    // For now, returning the color as-is
    return color;
};

/**
 * Gets all shades of a color family
 * @param family - 'primary', 'accent', or 'gray'
 * @returns Object with all shades of that color family
 */
export const getColorFamily = (family: 'primary' | 'accent' | 'gray') => {
    switch (family) {
        case 'primary':
            return PRIMARY_COLORS;
        case 'accent':
            return ACCENT_COLORS;
        case 'gray':
            return GRAY_COLORS;
        default:
            return PRIMARY_COLORS;
    }
};
